import { DownloadIcon, SearchIcon, SyncIcon } from "@primer/octicons-react";
import { Box } from "@primer/react";
import { Transition } from "semantic-ui-react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";

import { baoCaoHocPhiApi } from "../../api/baoCaoHocPhiApi";
import { khoanNopGroupApi } from "../../api/khoanNopGroupApi";
import { ComboboxHe } from "../../components/combobox-he";
import ComboboxKhoi from "../../components/combobox-khoi";
import { ComboboxTruong } from "../../components/combobox-truong";
import DataGrid from "../../components/data-grid";
import { IDataGridColumn } from "../../components/data-grid/type/IDataGridProps";
import { sf_khoannop_group } from "../../models/response/category/sf_khoannop_group";
import { RootState } from "../../state/reducers";
import Button from "../../components/ui/button";

const BaoCaoChiTietCongNoPage = () => {
  const [dm_truong_id, set_dm_truong_id] = useState<number>(0);
  const [dm_khoi_id, set_dm_khoi_id] = useState<number>(0);
  const [dm_he_id, set_dm_he_id] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { nam_hoc, dm_coso_id } = useSelector((x: RootState) => x.common);
  const [dataSourceAll, setDataSourceAll] = useState<any[]>([]);

  const [sf_khoannop_groups, set_sf_khoannop_groups] = useState<
    sf_khoannop_group[]
  >([]);

  const [isSearchSimple, setIsSearchSimple] = useState<boolean>(false);

  useEffect(() => {
    handleReloadKhoanNopGroup();
  }, []);

  const handleReloadKhoanNopGroup = async () => {
    const res = await khoanNopGroupApi.select_all();
    if (res.is_success) {
      const temp = res.data
        .filter((x: any) => x.dm_coso_id === dm_coso_id)
        .sort((a: any, b: any) => a.idx_report - b.idx_report);
      set_sf_khoannop_groups(temp);
    }
  };

  const handleSearch = () => {
    handleReloadReportAll();
  };

  const handleReloadReportAll = async () => {
    setIsLoading(true);
    try {
      const res = await baoCaoHocPhiApi.reportCongNoAll({
        nam_hoc: nam_hoc,
        dm_he_id: dm_he_id,
        dm_khoi_id: dm_khoi_id,
        dm_truong_id: dm_truong_id,
        dm_coso_id: dm_coso_id,
      });
      if (res.is_success) {
        setDataSourceAll(res.data || []);
      }
    } catch (error) {
      console.error("Error loading report:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (dataSourceAll !== undefined && dataSourceAll.length > 0) {
      setIsLoading(true);
      try {
        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataSourceAll);

        const headerMap: { [key: string]: string } = {
          ho_ten: "Họ tên",
          ma_hs: "Mã HS",
          ngay_sinh: "Ngày sinh",
          ten_khoi: "Khối",
          ten_he: "Hệ",
          QTBS_Noidung: "Nội dung",
          QTBS_TongTien_PN: "Tổng số tiền",
          QTHT_Noidung_MG: "Nội dung",
          QTHT_TongTien: "Tổng số tiền",
          Tong_cac_khoan_phai_nop_PN: "Tổng các khoản phải nộp",
          Tong_giam_tru_hoanTra_MG: "Tổng giảm trừ/hoàn trả",
          Tong_con_phai_dong: "Tổng còn phải đóng",
          Cac_lan_thuc_nop: "Các lần thực nộp",
          Tong_thuc_nop: "Tổng thực nộp",
          Chenh_lech_thua: "Chênh lệch thừa",
          Chenh_lech_thieu: "Chênh lệch thiếu",
        };

        const range = XLSX.utils.decode_range(worksheet["!ref"]!);
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_col(C) + "1";
          const cell = worksheet[cellAddress];
          if (cell && headerMap[cell.v as string]) {
            cell.v = headerMap[cell.v as string];
          }
        }

        const workbook: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "ReportData");

        XLSX.writeFile(workbook, "ReportCongNo.xlsx");
      } catch (error) {
        console.error("Error exporting Excel:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleExport = () => {
    handleExportExcel();
  };

  const dynamicColumns = useMemo((): IDataGridColumn[] => {
    const columns: IDataGridColumn[] = [];

    sf_khoannop_groups.forEach((x) => {
      columns.push({
        caption: x.ten_khoan_nop_group,
        align: "center" as const,
        columns: [
          {
            dataField: `KGR_DM_${x.id}`,
            caption: "Tiêu chuẩn",
            width: 100,
            align: "right" as const,
            cellRender: (data: any) => {
              const value = data[`KGR_DM_${x.id}`];
              return value != null && value != 0 && value !== "0" ? value?.toLocaleString("de-de") : "";
            },
          },
          {
            dataField: `KGR_MG_${x.id}`,
            caption: "Miễn giảm",
            width: 100,
            align: "right" as const,
            cellRender: (data: any) => {
              const value = data[`KGR_MG_${x.id}`];
              return value != null && value != 0 && value !== "0" ? value?.toLocaleString("de-de") : "";
            },
          },
          {
            dataField: `KGR_PN_${x.id}`,
            caption: "Phải nộp",
            width: 100,
            align: "right" as const,
            cellRender: (data: any) => {
              const value = data[`KGR_PN_${x.id}`];
              return value != null && value != 0 && value !== "0" ? value?.toLocaleString("de-de") : "";
            },
          },
          {
            dataField: `KGR_DN_${x.id}`,
            caption: "Đã nộp",
            width: 100,
            align: "right" as const,
            cellRender: (data: any) => {
              const value = data[`KGR_DN_${x.id}`];
              return value != null && value != 0 && value !== "0" ? value?.toLocaleString("de-de") : "";
            },
          },
          {
            dataField: `KGR_CT_${x.id}`,
            caption: "Còn thiếu",
            width: 100,
            align: "right" as const,
            cellRender: (data: any) => {
              const value = data[`KGR_CT_${x.id}`];
              return value != null && value != 0 && value !== "0" ? value?.toLocaleString("de-de") : "";
            },
          },
        ],
      });
    });

    return columns;
  }, [sf_khoannop_groups]);

  const simpleColumns: IDataGridColumn[] = useMemo(() => {
    return [
      {
        dataField: "ma_hs",
        caption: "Mã HS",
        width: 100,
        fixed: "left" as const,
      },
      {
        dataField: "ho_ten",
        caption: "Họ tên",
        width: 200,
        fixed: "left" as const,
      },
      {
        dataField: "ngay_sinh",
        caption: "Ngày sinh",
        width: 130,
        cellRender: (data: any) => {
          if (!data.ngay_sinh) return "";
          return new Date(data.ngay_sinh).toLocaleDateString("vi-VN");
        },
      },
      {
        dataField: "ten_khoi",
        caption: "Khối",
        width: 100,
        fixed: "left" as const,
      },
      {
        dataField: "ten_he",
        caption: "Hệ",
        width: 150,
        fixed: "left" as const,
      },
      {
        caption: "Tổng hợp",
        align: "center" as const,
        columns: [
          {
            dataField: "Tong_cac_khoan_phai_nop_PN",
            caption: "Tổng các khoản phải nộp",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => data.Tong_cac_khoan_phai_nop_PN != null && data.Tong_cac_khoan_phai_nop_PN != 0 && data.Tong_cac_khoan_phai_nop_PN !== "0" ? data.Tong_cac_khoan_phai_nop_PN?.toLocaleString("de-de") : "",
          },
          {
            dataField: "Tong_giam_tru_hoanTra_MG",
            caption: "Tổng giảm trừ/hoàn trả",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => data.Tong_giam_tru_hoanTra_MG != null && data.Tong_giam_tru_hoanTra_MG != 0 && data.Tong_giam_tru_hoanTra_MG !== "0" ? data.Tong_giam_tru_hoanTra_MG?.toLocaleString("de-de") : "",
          },
          {
            dataField: "Tong_con_phai_dong",
            caption: "Tổng còn phải đóng",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => data.Tong_con_phai_dong != null && data.Tong_con_phai_dong != 0 && data.Tong_con_phai_dong !== "0" ? data.Tong_con_phai_dong?.toLocaleString("de-de") : "",
          },
          {
            dataField: "Cac_lan_thuc_nop",
            caption: "Các lần thực nộp",
            width: 250,
          },
          {
            dataField: "Tong_thuc_nop",
            caption: "Tổng thực nộp",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => data.Tong_thuc_nop != null && data.Tong_thuc_nop != 0 && data.Tong_thuc_nop !== "0" ? data.Tong_thuc_nop?.toLocaleString("de-de") : "",
          },
          {
            dataField: "Chenh_lech_thua",
            caption: "Chênh lệch thừa",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => (
              <span style={{ color: data.Chenh_lech_thua > 0 ? "green" : "black", fontWeight: 600 }}>
                {data.Chenh_lech_thua != null && data.Chenh_lech_thua != 0 && data.Chenh_lech_thua !== "0" ? data.Chenh_lech_thua?.toLocaleString("de-de") : ""}
              </span>
            ),
          },
          {
            dataField: "Chenh_lech_thieu",
            caption: "Chênh lệch thiếu",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => (
              <span style={{ color: data.Chenh_lech_thieu > 0 ? "red" : "black", fontWeight: 600 }}>
                {data.Chenh_lech_thieu != null && data.Chenh_lech_thieu != 0 && data.Chenh_lech_thieu !== "0" ? data.Chenh_lech_thieu?.toLocaleString("de-de") : ""}
              </span>
            ),
          },
        ],
      },
    ];
  }, []);

  const detailColumns: IDataGridColumn[] = useMemo(() => {
    const baseColumns: IDataGridColumn[] = [
      {
        dataField: "ma_hs",
        caption: "Mã HS",
        width: 100,
        fixed: "left" as const,
      },
      {
        dataField: "ho_ten",
        caption: "Họ tên",
        width: 200,
        fixed: "left" as const,
      },
      {
        dataField: "ngay_sinh",
        caption: "Ngày sinh",
        width: 130,
        cellRender: (data: any) => {
          if (!data.ngay_sinh) return "";
          return new Date(data.ngay_sinh).toLocaleDateString("vi-VN");
        },
      },
      {
        dataField: "ten_khoi",
        caption: "Khối",
        width: 100,
        fixed: "left" as const,
      },
      {
        dataField: "ten_he",
        caption: "Hệ",
        width: 150,
        fixed: "left" as const,
      },
      ...dynamicColumns,
      {
        caption: "Quyết toán thu bổ sung",
        align: "center" as const,
        columns: [
          {
            dataField: "QTBS_Noidung",
            caption: "Nội dung",
            minWidth: 250,
          },
          {
            dataField: "QTBS_TongTien_PN",
            caption: "Tổng số tiền",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => data.QTBS_TongTien_PN != null && data.QTBS_TongTien_PN != 0 && data.QTBS_TongTien_PN !== "0" ? data.QTBS_TongTien_PN?.toLocaleString("de-de") : "",
          },
        ],
      },
      {
        caption: "Quyết toán thu hoàn trả",
        align: "center" as const,
        columns: [
          {
            dataField: "QTHT_Noidung_MG",
            caption: "Nội dung",
            minWidth: 250,
          },
          {
            dataField: "QTHT_TongTien",
            caption: "Tổng số tiền",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => data.QTHT_TongTien != null && data.QTHT_TongTien != 0 && data.QTHT_TongTien !== "0" ? data.QTHT_TongTien?.toLocaleString("de-de") : "",
          },
        ],
      },
      {
        caption: "Tổng hợp",
        align: "center" as const,
        columns: [
          {
            dataField: "Tong_cac_khoan_phai_nop_PN",
            caption: "Tổng các khoản phải nộp",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => data.Tong_cac_khoan_phai_nop_PN != null && data.Tong_cac_khoan_phai_nop_PN != 0 && data.Tong_cac_khoan_phai_nop_PN !== "0" ? data.Tong_cac_khoan_phai_nop_PN?.toLocaleString("de-de") : "",
          },
          {
            dataField: "Tong_giam_tru_hoanTra_MG",
            caption: "Tổng giảm trừ/hoàn trả",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => data.Tong_giam_tru_hoanTra_MG != null && data.Tong_giam_tru_hoanTra_MG != 0 && data.Tong_giam_tru_hoanTra_MG !== "0" ? data.Tong_giam_tru_hoanTra_MG?.toLocaleString("de-de") : "",
          },
          {
            dataField: "Tong_con_phai_dong",
            caption: "Tổng còn phải đóng",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => data.Tong_con_phai_dong != null && data.Tong_con_phai_dong != 0 && data.Tong_con_phai_dong !== "0" ? data.Tong_con_phai_dong?.toLocaleString("de-de") : "",
          },
          {
            dataField: "Cac_lan_thuc_nop",
            caption: "Các lần thực nộp",
            width: 250,
          },
          {
            dataField: "Tong_thuc_nop",
            caption: "Tổng thực nộp",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => data.Tong_thuc_nop != null && data.Tong_thuc_nop != 0 && data.Tong_thuc_nop !== "0" ? data.Tong_thuc_nop?.toLocaleString("de-de") : "",
          },
          {
            dataField: "Chenh_lech_thua",
            caption: "Chênh lệch thừa",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => (
              <span style={{ color: data.Chenh_lech_thua > 0 ? "green" : "black", fontWeight: 600 }}>
                {data.Chenh_lech_thua != null && data.Chenh_lech_thua != 0 && data.Chenh_lech_thua !== "0" ? data.Chenh_lech_thua?.toLocaleString("de-de") : ""}
              </span>
            ),
          },
          {
            dataField: "Chenh_lech_thieu",
            caption: "Chênh lệch thiếu",
            width: 150,
            align: "right" as const,
            cellRender: (data: any) => (
              <span style={{ color: data.Chenh_lech_thieu > 0 ? "red" : "black", fontWeight: 600 }}>
                {data.Chenh_lech_thieu != null && data.Chenh_lech_thieu != 0 && data.Chenh_lech_thieu !== "0" ? data.Chenh_lech_thieu?.toLocaleString("de-de") : ""}
              </span>
            ),
          },
        ],
      },
    ];

    return baseColumns;
  }, [dynamicColumns]);

  const columns = isSearchSimple ? simpleColumns : detailColumns;

  return (
    <Box>
      <Transition
        animation="scale"
        visible={true}
        duration={100}
        transitionOnMount={true}
      >
        <Box sx={{ px: 1, mt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <h4 style={{ marginBottom: "10px" }}>Báo cáo chi tiết công nợ</h4>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "5px" }}>
              <div className="form-group" style={{ marginRight: "5px" }}>
                <ComboboxTruong
                  value={dm_truong_id}
                  isShowClearButton={true}
                  size="small"
                  onValueChanged={(value) => {
                    set_dm_truong_id(value || 0);
                  }}
                />
              </div>
              <div className="form-group" style={{ marginRight: "5px" }}>
                <ComboboxKhoi
                  width={150}
                  value={dm_khoi_id}
                  dm_truong_id={dm_truong_id}
                  isShowClearButton={true}
                  size="small"
                  onValueChanged={(e: any) => {
                    set_dm_khoi_id(e || 0);
                  }}
                />
              </div>
              <div className="form-group" style={{ marginRight: "5px" }}>
                <ComboboxHe
                  width={200}
                  value={dm_he_id}
                  dm_truong_id={dm_truong_id}
                  dm_khoi_id={dm_khoi_id}
                  isShowClearButton={true}
                  size="small"
                  onValueChanged={(value) => {
                    set_dm_he_id(value || 0);
                  }}
                />
              </div>
              <div className="form-group" style={{ marginRight: "5px" }}>
                <Button
                  text="Tổng hợp"
                  leadingVisual={SearchIcon}
                  variant="default"
                  size="small"
                  isLoading={isLoading}
                  onClick={() => {
                    setIsSearchSimple(true);
                    handleSearch();
                  }}
                />
              </div>
              <div className="form-group" style={{ marginRight: "5px" }}>
                <Button
                  text="Tổng hợp chi tiết"
                  leadingVisual={SyncIcon}
                  variant="default"
                  size="small"
                  isLoading={isLoading}
                  onClick={() => {
                    setIsSearchSimple(false);
                    handleSearch();
                  }}
                />
              </div>
              <div className="form-group" style={{ marginRight: "5px" }}>
                <Button
                  text="Export"
                  leadingVisual={DownloadIcon}
                  variant="default"
                  size="small"
                  isLoading={isLoading}
                  onClick={handleExport}
                  disabled={dataSourceAll === undefined || dataSourceAll.length === 0}
                />
              </div>
            </Box>
          </Box>

          <DataGrid
            keyExpr="id"
            dataSource={dataSourceAll}
            height={window.innerHeight - 210}
            filterRow={{ enable: true }}
            paging={{ enable: true, pageSize: 50, pageSizeItems: [10, 50, 200, 500] }}
            sorting={{ enable: true }}
            columns={columns}
            title=""
          />
        </Box>
      </Transition>
    </Box>
  );
};

export default BaoCaoChiTietCongNoPage;
